// =============================================
// applyLinkFixes — atomicy DB+WP apply dla zaznaczonych fix'ów w modalu.
// =============================================
//
// Wywoływane TYLKO po user kliknie "Zastosuj zaznaczone" w LinkFixPreviewModal.
//
// Algorytm (surgical, atomic 2-step z rollback):
//   1. Pobierz oldHtml + wp_post_id z thematic_node_content
//   2. Build newHtml: per-fix surgical literal replace (matchedHtml → buildReplacement)
//   3. DB UPDATE final_html (jeden round-trip)
//   4. WP REST updatePost(wp_post_id, { content: newHtml }) — selective field
//   5. JEŚLI WP fail → ROLLBACK DB do oldHtml (restore source of truth)
//   6. Log do wp_action_logs (audit trail)
//
// Reuse:
//   - setupWpForMap z healPendingLinks (eksportowane STOP-2)
//   - wordpressService.updatePost — selective content-only update
//   - DeadLink type z deadLinkDetector

import { supabase } from "@/integrations/supabase/client";
import { wordpressService } from "@/services/wordpressService";
import { logPublic } from "@/services/contentEngine/contentLogger";
import { setupWpForMap } from "@/services/healPendingLinks";
import type { DeadLink } from "./deadLinkDetector";

export interface ApplyLinkFixesResult {
  /** DB UPDATE udany */
  dbUpdated: boolean;
  /** WP UPDATE udany (false jeśli rollback wykonany) */
  wpUpdated: boolean;
  /** Ile fix'ów faktycznie zostało zaaplikowanych (matched + replaced) */
  fixedCount: number;
  /** Ile fix'ów nie znalazło matchedHtml w aktualnym final_html (stale data) */
  notMatchedCount: number;
  /** Error message gdy coś padło (dbErr lub wpErr) */
  error?: string;
  /** Czy rollback DB do oldHtml został wykonany (po WP fail) */
  rolledBack?: boolean;
}

/**
 * Apply zaznaczonych fix'ów do final_html (DB) + content (WP) atomically.
 *
 * @param nodeId — UUID noda (źródło zmian)
 * @param mapId — UUID mapy (potrzebne do setupWpForMap)
 * @param fixes — DeadLink[] z zaznaczonymi w modalu (filtruje tylko z suggestedAction)
 */
export async function applyLinkFixes(
  nodeId: string,
  mapId: string,
  fixes: DeadLink[],
): Promise<ApplyLinkFixesResult> {
  // 0. Filter: tylko fixy z określoną akcją (skip "raw" deads bez sugestii)
  const actionable = fixes.filter((f) => !!f.suggestedAction);
  if (actionable.length === 0) {
    return {
      dbUpdated: false,
      wpUpdated: false,
      fixedCount: 0,
      notMatchedCount: 0,
    };
  }

  // 1. Pobierz aktualny final_html + wp_post_id
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: row, error: fetchErr } = await (supabase as any)
    .from("thematic_node_content")
    .select("final_html, wp_post_id")
    .eq("node_id", nodeId)
    .maybeSingle();
  if (fetchErr || !row) {
    return {
      dbUpdated: false,
      wpUpdated: false,
      fixedCount: 0,
      notMatchedCount: 0,
      error: `Fetch failed: ${fetchErr?.message || "node content not found"}`,
    };
  }
  const oldHtml: string = row.final_html || "";
  const wpPostId: number | null = row.wp_post_id ?? null;

  // 2. Surgical replace per fix
  let newHtml = oldHtml;
  let fixedCount = 0;
  let notMatchedCount = 0;
  for (const fix of actionable) {
    if (!newHtml.includes(fix.matchedHtml)) {
      // Stale fix — HTML się zmienił między scan'em a apply (rzadkie, ale safety)
      notMatchedCount++;
      continue;
    }
    const replacement = buildReplacement(fix);
    // String.replace zamienia tylko pierwsze wystąpienie literal (no regex flag)
    newHtml = newHtml.replace(fix.matchedHtml, replacement);
    fixedCount++;
  }

  if (fixedCount === 0) {
    return {
      dbUpdated: false,
      wpUpdated: false,
      fixedCount: 0,
      notMatchedCount,
      error:
        notMatchedCount > 0
          ? "Wszystkie fixy są stale (HTML się zmienił) — odśwież scan"
          : "Brak fix'ów do zastosowania",
    };
  }

  // 3. DB UPDATE
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: dbErr } = await (supabase as any)
    .from("thematic_node_content")
    .update({ final_html: newHtml })
    .eq("node_id", nodeId);
  if (dbErr) {
    return {
      dbUpdated: false,
      wpUpdated: false,
      fixedCount: 0,
      notMatchedCount,
      error: `DB update failed: ${dbErr.message}`,
    };
  }

  // 4. WP UPDATE
  if (!wpPostId) {
    // Bez wp_post_id nie ma jak updateować WP — DB jest spójna, WP zostaje stara
    logPublic(
      "LINK_AUDIT",
      `Node ${nodeId.slice(0, 8)}: DB updated (${fixedCount} fixes), brak wp_post_id — WP NIE zaktualizowany`,
    );
    return {
      dbUpdated: true,
      wpUpdated: false,
      fixedCount,
      notMatchedCount,
      error: "wp_post_id null — DB zapisane, WP NIE zaktualizowany",
    };
  }

  try {
    await setupWpForMap(mapId);
    const wpRes = await wordpressService.updatePost(wpPostId, {
      content: newHtml,
    });
    if (!wpRes.success) {
      // ROLLBACK DB do oldHtml — żeby DB ≡ WP (source of truth zachowany)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any)
        .from("thematic_node_content")
        .update({ final_html: oldHtml })
        .eq("node_id", nodeId);
      logPublic(
        "LINK_AUDIT",
        `Node ${nodeId.slice(0, 8)}: WP fail → ROLLBACK DB. Error: ${wpRes.error || "unknown"}`,
      );
      return {
        dbUpdated: false,
        wpUpdated: false,
        fixedCount: 0,
        notMatchedCount,
        rolledBack: true,
        error: `WP update failed (rollback OK): ${wpRes.error || "unknown"}`,
      };
    }
  } catch (e) {
    // WP rzucił → ROLLBACK
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any)
      .from("thematic_node_content")
      .update({ final_html: oldHtml })
      .eq("node_id", nodeId);
    const msg = e instanceof Error ? e.message : String(e);
    logPublic(
      "LINK_AUDIT",
      `Node ${nodeId.slice(0, 8)}: WP threw → ROLLBACK DB. Error: ${msg}`,
    );
    return {
      dbUpdated: false,
      wpUpdated: false,
      fixedCount: 0,
      notMatchedCount,
      rolledBack: true,
      error: `WP threw (rollback OK): ${msg}`,
    };
  }

  // 5. Sukces — log + return
  logPublic(
    "LINK_AUDIT",
    `Node ${nodeId.slice(0, 8)}: ${fixedCount} linków naprawione (DB + WP)${notMatchedCount > 0 ? `, ${notMatchedCount} stale` : ""}`,
  );

  return {
    dbUpdated: true,
    wpUpdated: true,
    fixedCount,
    notMatchedCount,
  };
}

// =============================================
// Build replacement HTML per fix.suggestedAction
// =============================================

export function buildReplacement(fix: DeadLink): string {
  switch (fix.suggestedAction) {
    case "replace_href":
      // <a href="${wp_post_url}">${anchor}</a> (kanoniczny absolute URL)
      if (!fix.suggestedHref) return fix.matchedHtml; // safety — no-op
      return `<a href="${fix.suggestedHref}">${fix.anchor}</a>`;

    case "replace_with_marker":
      // <span data-pending-link="/slug">${anchor}</span> (zostanie healed po publish targetu)
      if (!fix.suggestedSlug) return fix.matchedHtml; // safety
      return `<span data-pending-link="/${fix.suggestedSlug}">${fix.anchor}</span>`;

    case "strip_to_text":
      // <span>${anchor}</span> — NEUTRALNY tekst (NIE <strong>, zgodnie z akceptem 2026-06-09 #2)
      return `<span>${fix.anchor}</span>`;

    default:
      return fix.matchedHtml; // unknown action — no-op
  }
}
